// ── usePolymarketRelayOrders ────────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/orders (the org-key
// trader proxy). Returns a flat state + actions object so a single hook
// invocation is enough for "show me my live orders + let me cancel one."
//
// Polling cadence matches kalshi's accountStore (5s default). When the
// relay returns an unavailable code (NOT_IN_ORG / PERSONAL_KEY_MODE /
// NO_POLY_CRED) the hook is DEBOUNCED and SELF-HEALING, not one-shot:
// a mount already rendering orders holds steady until the code proves
// persistent (UNAVAILABLE_FLIP_STREAK consecutive coded results — a
// credential edit makes resolvePolyContext throw a transient
// NoPolyCredentialsError from a live DB read), while a mount with no
// data flips immediately (orgs without poly creds must not stare at a
// spinner for three polls). Once unavailable, polling slows to a
// re-probe every UNAVAILABLE_REPROBE_TICKS-th tick instead of stopping,
// so an admin re-adding the cred heals every mount without a manual
// refresh. `refresh()` still works in that state for explicit re-probes.
//
// The underlying list fetch is MODULE-SHARED (see listOpenOrdersShared)
// so concurrent mounts — PositionTracker + its docked sidebar prong —
// coalesce onto one relay call per poll window instead of multiplying
// the org's read traffic.
//
// Track 3 scope: open orders only. Fills/positions need a server-side
// poller + schema work — tracked in the defer note at the galactus root.

import { useCallback, useEffect, useRef, useState } from 'react';
import { sourceConfirmationError } from '@/lib/trading/sourceConfirmation';
import {
  cancelPolyOrderViaRelay,
  listPolyOpenOrdersViaRelay,
  placePolyOrderViaRelay,
  polyCancelOutcome,
  polyOpenOrderRemaining,
  type PolyRelayOpenOrder,
  type PolyRelayUnavailableCode,
} from '@/lib/polymarket/relay';
import {
  cancelPolyOrderOnce,
  reconcilePolyCancels,
  setPolyCancelIdentity,
} from '@/lib/trading/bulkOrderCancel';
import { describePolyOrder } from '@/lib/polymarket/describeOrder';
import { explainPolyError } from '@/lib/polymarket/errors';
import { validateReplacePriceCents } from '@/lib/pricing/subCent';
import { resolveTickCents } from '@/lib/polymarket/tickSize';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';
import { useAuthStore } from '@/stores/authStore';
import {
  EDIT_REPLACEMENT_UNKNOWN_MESSAGE,
  EDIT_WOULD_CROSS_MESSAGE,
  postOnlyWouldCross,
  useAccountStore,
} from '@/stores/accountStore';
import { useMarketStore } from '@/stores/marketStore';
import { polyAccountReadScope, capturePolyActionScope } from '@/lib/polymarket/accountScope';
import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import { polyExpirationMs, polyTifLabel } from '@/components/cards/orderCardModel';
import { readSoundSettings } from '@/lib/audio/soundSettings';
import { getChannelVolume } from '@/lib/alerts/alertPreferences';
import { playOrderPlacedTone, playOrderRejectedTone } from '@/lib/audio/tradeAudio';
import type { PlacePolyOrderRelayResult } from '@/lib/polymarket/relay';

/** Polymarket cancels a good-till-date order one minute before its stated
 *  expiry and refuses one placed inside that minute. A replacement keeps the
 *  original's expiry, so an order this close to it cannot be re-placed: the
 *  extra ten seconds cover the cancel round trip and clock skew. */
const POLY_GTD_REPLACE_MIN_LEAD_MS = 70_000;

/** Same channel and preference as a Kalshi refusal. */
function ringOrderRejected(): void {
  const snd = readSoundSettings();
  if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
}

/** Relay sentences that arrive on a 5xx without a code the client keeps but
 *  still prove nothing was placed: the venue's own 4xx refusal (the relay
 *  wraps it in a 502 as `Polymarket API 4xx: ...`), and the relay refusing
 *  before the post because trading capacity or account authority could not
 *  be confirmed. Anything else on a 408/5xx may have reached the venue. */
const POLY_DEFINITE_REFUSAL_ERRORS: readonly RegExp[] = [
  /^Polymarket API 4\d\d\b/,
  /^Trading capacity or account authority could not be confirmed\b/,
];

/**
 * What a Polymarket placement answer proves, by the rule Kalshi placements
 * follow: `placed` (the relay accepted it), `refused` (a definite answer that
 * nothing was placed), or `unknown` (the order may be live: a lost response,
 * the relay's POLY_ORDER_STATE_UNKNOWN, or any 408/5xx that does not carry a
 * known refusal: a venue 5xx, which the relay treats as possibly accepted and
 * sweeps for, or a venue error with no status, which proves nothing either way).
 */
export function polyPlacementOutcome(out: PlacePolyOrderRelayResult): 'placed' | 'refused' | 'unknown' {
  if (out.ok) return 'placed';
  if (out.status === 0 || out.code === 'POLY_ORDER_STATE_UNKNOWN') return 'unknown';
  if (
    out.code === undefined &&
    (out.status === 408 || out.status >= 500) &&
    !POLY_DEFINITE_REFUSAL_ERRORS.some((re) => re.test(out.error))
  ) {
    return 'unknown';
  }
  return 'refused';
}

/** orderPlaced on an acceptance, orderRejected on a refusal, nothing on an
 *  unknown result. Respects each channel's preference and volume. */
export function ringPolyPlacement(outcome: 'placed' | 'refused' | 'unknown'): void {
  if (outcome === 'unknown') return;
  const snd = readSoundSettings();
  if (outcome === 'placed') {
    if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
  } else if (snd.orderRejectionSoundEnabled) {
    playOrderRejectedTone(getChannelVolume('orderRejected'));
  }
}

/** The live-book crossing read for a Polymarket order, keyed on the order's
 *  own token: the market's YES token reads as the YES side, its NO token as
 *  the NO side. Null when the market or a usable book is not held. */
function polyReplacementWouldCross(
  tokenId: string,
  pmTicker: string | undefined,
  action: 'buy' | 'sell',
  priceCents: number,
): boolean | null {
  if (!pmTicker) return null;
  const market = useMarketStore.getState().getMarket(pmTicker);
  const side = market?.yesTokenId === tokenId ? 'yes' : market?.noTokenId === tokenId ? 'no' : null;
  if (side == null) return null;
  return postOnlyWouldCross(pmTicker, side, action, priceCents);
}

export type PolyRelayOrdersStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyRelayOrdersState {
  orders: PolyRelayOpenOrder[];
  status: PolyRelayOrdersStatus;
  /** Latest completed read confirms these rows; display debouncing alone
   * cannot establish this. Optimistic changes also await confirmation. */
  snapshotConfirmed: boolean;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  lastFetchMs: number | null;
}

/** Hook-private: every applied result records the authenticated identity
 *  that produced it. A stamp mismatch is resolved during RENDER (not in an
 *  effect) so a previous account's orders are never returnable, not even
 *  for the one render before an effect could clear them. */
interface StampedOrdersState extends PolyRelayOrdersState {
  identity: string;
}

const IDLE_ORDERS_STATE: PolyRelayOrdersState = {
  orders: [],
  status: 'idle',
  snapshotConfirmed: false,
  error: null,
  unavailableCode: null,
  lastFetchMs: null,
};

export interface UsePolymarketRelayOrdersOptions {
  /** Off → hook is inert (no fetch, no poll). Defaults to true. */
  enabled?: boolean;
  /** Poll interval in ms. Defaults to 5000. */
  pollMs?: number;
}

/** Hints the caller passes through to `replace()` so the new-place leg
 *  carries the same market-specific config the original order needed.
 *  Optional because the relay has sensible defaults — but a NEG_RISK
 *  market WILL reject a re-place without `negRisk: true`, so consumers
 *  that know the market context should pass these through. */
export interface PolyRelayReplaceOptions {
  tickSize?: string;
  negRisk?: boolean;
  /** Override the new order's size. Omitted → use the original order's
   *  remaining size (effectively price-only edit). Polymarket has no
   *  queue-preserving decrease equivalent to Kalshi's /decrease endpoint,
   *  so any size change always goes through cancel + new-place. */
  nextQuantity?: number;
}

export interface UsePolymarketRelayOrdersReturn extends PolyRelayOrdersState {
  /** `refresh(true)` forces a network fetch (post-mutation reconciles,
   *  explicit Refresh buttons); default rides the shared freshness window. */
  refresh: (force?: boolean) => Promise<void>;
  cancel: (orderId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Cancel + immediately re-place an open Polymarket order at a new price.
   *  Derives tokenId, action, and remaining contracts from the existing
   *  open-order; caller provides `newPriceCents` and optional tickSize /
   *  negRisk hints. Polymarket has no atomic amend — there's a brief window
   *  between the cancel and the new place where the order isn't on the book.
   *  Success carries the freshly placed order's id so callers keying UI
   *  state off the order id (pinned cards) can transfer to the new order. */
  replace: (
    orderId: string,
    newPriceCents: number,
    options?: PolyRelayReplaceOptions,
  ) => Promise<{ ok: true; orderId: string } | { ok: false; error: string }>;
}

const DEFAULT_POLL_MS = 5000;
// A coded 404 can be TRANSIENT (credential edit: resolvePolyContext throws
// NoPolyCredentialsError from a live DB read before clientCache.set), and the
// shared in-flight fetch hands one such 404 to EVERY coalesced mount. A mount
// already rendering orders therefore holds status+orders until the answer
// proves persistent: this many CONSECUTIVE coded results (~15s at 5s polls).
const UNAVAILABLE_FLIP_STREAK = 3;
// A syntactically valid 200/[] is not always authoritative in practice: the
// Polymarket account projection can briefly return an empty list and recover on
// the next poll. One such snapshot must not erase actionable orders from the
// trader's screen, but a real flatten must still clear promptly. Starting from
// visible rows, require this many CONSECUTIVE successful empty reads; with the
// default cadence the maximum extra visibility is one 5s poll interval.
const EMPTY_CONFIRM_STREAK = 2;
// While unavailable, re-probe every Nth poll tick (30s at 5s polls) instead
// of stopping — self-heals after an admin re-adds the cred. Unavailable
// results never enter the shared cache and concurrent probes coalesce, so
// the extra traffic stays bounded.
const UNAVAILABLE_REPROBE_TICKS = 6;

// ── Module-shared list fetch (the usePolyBalance idiom) ─────────
// The hook mounts on several surfaces at once — PositionTracker plus its
// docked PositionSidebar prong, and the ValueDashboard / GameDetail
// overlay sidebars — and each mount is a 5s poller. Without sharing,
// every extra mount is another /api/polymarket/orders round-trip on the
// org's read quota. Freshness is caller-relative (a fraction of the
// mount's own pollMs) so a lone mount still refreshes every tick while
// N same-cadence mounts coalesce onto one fetch per window. Only OK
// results cache: unavailable answers must never serve from cache — each
// mount's slow re-probe and any manual refresh() must hit the relay.
const SHARE_FRACTION = 0.8;

type ListOrdersResult = Awaited<ReturnType<typeof listPolyOpenOrdersViaRelay>>;

// Generation counter: invalidation must also disown any fetch already on
// the wire — a pre-invalidation response is a PRE-MUTATION snapshot, and
// letting its .then re-cache it "fresh" resurrects a just-cancelled order
// for every mount for a full share window (a double-cancel invitation on
// real money). A fetch records the generation it started under and only
// writes the cache if that generation is still current; joiners never
// attach to a stale-generation in-flight promise.
// Identity (`${userId}:${orgId}`) additionally owns every shared slot: a
// snapshot fetched for org A must never satisfy org B's read, not even
// inside the freshness window, and B must never join A's in-flight fetch.
// _gen stays GLOBAL so cancel/replace invalidation keeps disowning every
// pre-mutation response exactly as before; _activeIdentity stops a late A
// response from re-caching itself after B has already taken over.
let _gen = 0;
let _activeIdentity: string | null = null;
/**
 * #748 review — a monotonic id for each SUCCESSFUL NETWORK RESPONSE.
 *
 * `fetched` answers "did this call touch the wire", which is a question about
 * the CALLER. Empty-list confirmation needs a question about the VENUE: "is
 * this a reading I have not already counted?" Those differ precisely when one
 * mount wins every read and another is served from cache — the quiet mount
 * never advanced its streak and could hold a vanished order indefinitely.
 *
 * A cache hit returns the id of the response that filled it, so repeated hits
 * are the same piece of evidence and are counted once. Assigned only on a
 * current-generation, current-identity success, so a pre-mutation or
 * wrong-account response never becomes evidence at all.
 */
let _readSeq = 0;
let _cached: {
  identity: string;
  at: number;
  result: ListOrdersResult;
  seq: number;
} | null = null;
let _inflight: {
  identity: string;
  gen: number;
  promise: Promise<ListOrdersResult>;
  /** Filled when the response lands, so every coalesced awaiter — not just the
   *  mount that started the fetch — reads the same id. */
  box: { seq: number };
} | null = null;

/** `fetched` distinguishes a real network round-trip (possibly shared
 *  with a concurrent mount) from a cache hit — consumers stamp
 *  lastFetchMs only for the former. maxAgeMs 0 forces the network (still
 *  coalescing with a CURRENT-generation in-flight fetch). `stale` means
 *  an invalidation landed while this response was on the wire — it is a
 *  pre-mutation snapshot and must never reach the mount's visible state
 *  (the generation guard already kept it out of the cache; per-mount
 *  state needs the same treatment). Lookups are additionally scoped to
 *  `identity` — see the shared-slot declarations above. */
async function listOpenOrdersShared(
  identity: string,
  maxAgeMs: number,
): Promise<{
  result: ListOrdersResult;
  fetched: boolean;
  stale: boolean;
  /** Id of the venue reading behind this result; 0 when there is none (a
   *  failed read is not evidence). Equal across every mount served by the same
   *  response, whether it fetched, coalesced, or hit the cache. */
  readSeq: number;
}> {
  _activeIdentity = identity;

  if (_cached?.identity === identity && Date.now() - _cached.at < maxAgeMs) {
    // Cache writes are generation-gated, so a cache hit is never stale. It
    // carries the id of the response that filled it: the same evidence, not a
    // second sighting of it.
    return { result: _cached.result, fetched: false, stale: false, readSeq: _cached.seq };
  }
  let gen: number;
  let promise: Promise<ListOrdersResult>;
  let box: { seq: number };
  // Same-identity coalescing only: a different-identity in-flight fetch is
  // another account's read, so B starts its own and takes the slot.
  if (_inflight?.identity === identity && _inflight.gen === _gen) {
    ({ gen, promise, box } = _inflight);
  } else {
    // Any in-flight fetch from a stale generation is left to finish on
    // its own (it can no longer cache); start a fresh, current-gen fetch.
    gen = _gen;
    const ownBox = { seq: 0 };
    box = ownBox;
    promise = listPolyOpenOrdersViaRelay()
      .then((out) => {
        if (out.ok && gen === _gen && _activeIdentity === identity) {
          _readSeq += 1;
          ownBox.seq = _readSeq;
          _cached = { identity, at: Date.now(), result: out, seq: _readSeq };
        }
        return out;
      })
      .finally(() => {
        // Promise-identity check: A's late finally must not clear B's slot.
        if (_inflight?.promise === promise) _inflight = null;
      });
    _inflight = { identity, gen, promise, box };
  }
  const result = await promise;
  // A stale-generation result is never evidence, whatever it contains.
  return { result, fetched: true, stale: gen !== _gen, readSeq: gen === _gen ? box.seq : 0 };
}

/** Mutations (cancel/replace) drop the snapshot AND disown in-flight
 *  fetches so their follow-up refresh reads authoritative post-mutation
 *  state — never a pre-mutation response that was already on the wire. */
function invalidateSharedOrders(): void {
  _gen += 1;
  _cached = null;
}

/** Mutation-scoped invalidation for callers that cancel via the relay
 *  DIRECTLY (PositionTracker's sweep cancel-all and poly-close flows)
 *  rather than through this hook's cancel(): drops the snapshot and
 *  disowns in-flight fetches so their forced reconcile can't coalesce
 *  onto a same-generation pre-mutation poll and re-show every order the
 *  sweep just cancelled. */
export function invalidatePolyOrdersShare(): void {
  invalidateSharedOrders();
}

/** Purge the shared snapshot and disown any in-flight fetch. Registered in
 *  authStore's purgeUserCaches next to the headroom/POS/rate-limit resets:
 *  after a logout or user/org switch the previous ACCOUNT's Polymarket
 *  orders must never serve the next session, not even for one cycle. */
export function resetPolyOrdersShare(): void {
  _gen += 1;
  _activeIdentity = null;
  _cached = null;
  _inflight = null;
}

export function _resetPolyOrdersShareForTests(): void {
  resetPolyOrdersShare();
}

export function usePolymarketRelayOrders(
  options: UsePolymarketRelayOrdersOptions = {},
): UsePolymarketRelayOrdersReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;

  // Same composed identity the positions hook selects on.
  useAccountStore(polyAccountReadScope);
  const identity = useAuthStore(polyAccountReadScope);

  // #272 — bind the cross-surface Polymarket cancel fence to this account.
  // A fence entry describes ONE account's order; on a switch the claims are
  // dropped, so the new account's snapshots can never retire them (and vice
  // versa). Done during render, before any fetch result can be reconciled.
  setPolyCancelIdentity(identity);

  const [stamped, setStamped] = useState<StampedOrdersState>(() => ({
    ...IDLE_ORDERS_STATE,
    identity,
  }));
  // The safety boundary: a mismatched stamp reads as idle THIS render.
  const current: PolyRelayOrdersState =
    stamped.identity === identity ? stamped : IDLE_ORDERS_STATE;

  // Ref guard so multiple in-flight fetches don't trample each other's
  // state when the user manually refreshes during a tick. Owned by the
  // IDENTITY that started the request (Danielle R2 DNL-R1-001 / P1,
  // feed-filters-per-view): an org switch must start the new identity's
  // read at once even while the previous identity's request is still on the
  // wire — that request is stamp-fenced and never joins the shared slot
  // under the new identity, and a hung relay must not hold the latch
  // against the mounted account. Same-identity calls still coalesce.
  const inFlightRef = useRef<string | null>(null);
  // Per-identity request generation (A→B→A can overlap two A requests):
  // only the latest started request for an identity may apply or release.
  const requestGenRef = useRef(0);
  const latestGenByIdentityRef = useRef(new Map<string, number>());
  // The identity this mount is CURRENTLY rendering, readable from an older
  // request's loop: a queued trailing pass belongs to the mounted identity,
  // never to a request that outlived its account.
  const currentIdentityRef = useRef(identity);
  useEffect(() => { currentIdentityRef.current = identity; }, [identity]);
  // A FORCED call landing mid-flight must not be dropped — the caller just
  // mutated the book and the fetch on the wire predates it. Any number of
  // forces collapse into ONE trailing forced pass after the current one.
  const queuedForceRef = useRef(false);
  // Synchronous mirror of the stamped state: the unavailable debounce decides
  // off "what is this mount showing RIGHT NOW", and render-cycle timing must
  // not blur that (a queued trailing pass processes two results in one
  // callback). Every visible-state write goes through the apply* helpers.
  const stateRef = useRef<StampedOrdersState>(stamped);
  // Consecutive coded-404 results (stale-generation results never reach this
  // point — they're discarded and refetched pre-setState). Code transitions
  // (NO_POLY_CRED → ONLY_LEGACY_EOA_CRED) keep incrementing: still "poly
  // unavailable", just a different reason.
  const unavailableStreakRef = useRef(0);
  // Consecutive successful 200/[] results observed while this identity still
  // has visible rows. Errors and unavailable results break the sequence.
  const emptyStreakRef = useRef(0);
  // #748 review — the last venue reading THIS mount has already counted toward
  // that streak. Keyed on the shared response id rather than on whether this
  // mount fetched, so a mount served from cache still advances once per
  // distinct reading, and a mount hit repeatedly by the same reading does not
  // advance twice. Per mount by design: two mounts must each confirm for
  // themselves, which is what keeps the two-distinct-read requirement intact.
  const countedReadSeqRef = useRef(0);
  // Poll ticks observed while unavailable — every UNAVAILABLE_REPROBE_TICKS-th
  // one re-probes.
  const unavailableTicksRef = useRef(0);

  // Read the mount's own state ONLY when it belongs to the current identity;
  // otherwise the caller sees the idle baseline. Callbacks read this at call
  // time, so they stay identity-stable instead of re-creating every fetch.
  const carry = useCallback(
    (): PolyRelayOrdersState =>
      stateRef.current.identity === identity ? stateRef.current : IDLE_ORDERS_STATE,
    [identity],
  );

  const apply = useCallback(
    (next: PolyRelayOrdersState) => {
      // A result for an identity this mount no longer renders is dropped
      // (it would otherwise replace the mounted account's stamped state and
      // render as idle until the next poll); the stamp stays as defense in
      // depth for anything that slips.
      if (currentIdentityRef.current !== identity) return;
      const resolved: StampedOrdersState = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
    },
    [identity],
  );

  const applyOrders = useCallback(
    (nextOrders: PolyRelayOpenOrder[]) => {
      apply({ ...carry(), orders: nextOrders, snapshotConfirmed: false });
    },
    [apply, carry],
  );

  const refresh = useCallback(async (force = false) => {
    if (inFlightRef.current === identity) {
      if (force) queuedForceRef.current = true;
      return;
    }
    const myGen = ++requestGenRef.current;
    latestGenByIdentityRef.current.set(identity, myGen);
    inFlightRef.current = identity;
    const superseded = (): boolean => latestGenByIdentityRef.current.get(identity) !== myGen;
    if (carry().status === 'idle') {
      apply({ ...carry(), status: 'loading' });
    }
    try {
      let runForce = force;
      do {
        queuedForceRef.current = false;
        // force → bypass the freshness window (post-mutation reconciles and
        // the user's explicit Refresh must never serve a pre-mutation
        // snapshot); still coalesces with a current-gen in-flight fetch.
        let { result: out, fetched, stale, readSeq } = await listOpenOrdersShared(
          identity,
          runForce ? 0 : pollMs * SHARE_FRACTION,
        );
        // Drop BEFORE any evidence is touched (Danielle R3, DNL-R1-001 / P1):
        // a completion for an identity this mount no longer renders, or one a
        // newer same-identity request has superseded, must not cast a vote in
        // the unavailable / empty streaks, advance the read-sequence or poll
        // counters, reconcile cancel fences, or write state. Every streak is
        // reset on identity change (the polling effect), so with this drop no
        // stale identity can reach them.
        if (superseded() || currentIdentityRef.current !== identity) return;
        if (stale) {
          // The response predates an invalidation — a pre-mutation snapshot
          // must never render (it would resurrect a just-cancelled order in
          // this mount's UI). One forced retry reads the post-mutation
          // truth, coalescing with the mutation's own reconcile fetch.
          // Take the REFETCH's id too: keeping the stale one would let a
          // pre-mutation reading masquerade as evidence, or block the real one
          // that follows it from being counted.
          ({ result: out, fetched, readSeq } = await listOpenOrdersShared(identity, 0));
          if (superseded() || currentIdentityRef.current !== identity) return;
        }
        const nextLastFetchMs = fetched ? Date.now() : carry().lastFetchMs;
        if (out.ok) {
          unavailableStreakRef.current = 0;
          const visible = carry();
          const confirmingVisibleEmpty =
            out.orders.length === 0 && visible.orders.length > 0;
          if (!confirmingVisibleEmpty) {
            emptyStreakRef.current = 0;
            countedReadSeqRef.current = readSeq;
          } else if (readSeq !== 0 && readSeq !== countedReadSeqRef.current) {
            // A distinct venue reading this mount has not counted yet. Repeated
            // cache hits carry the id of the response that filled the cache, so
            // they are the same evidence and advance nothing — but a mount that
            // never wins a fetch still advances once per reading, which is what
            // stops a quiet mount holding a vanished order forever while a busy
            // one refreshes (#748 review).
            countedReadSeqRef.current = readSeq;
            emptyStreakRef.current += 1;
          }
          const holdUnconfirmedEmpty =
            confirmingVisibleEmpty && emptyStreakRef.current < EMPTY_CONFIRM_STREAK;

          if (holdUnconfirmedEmpty) {
            // This was still a real successful round-trip, so advance the
            // fetch clock while retaining the last confirmed actionable rows.
            // Do NOT reconcile cancel fences from an unconfirmed empty: doing
            // so would treat the same suspect snapshot as authoritative in a
            // different safety subsystem.
            apply({
              ...visible,
              status: 'ready',
              snapshotConfirmed: false,
              error: null,
              unavailableCode: null,
              lastFetchMs: nextLastFetchMs,
            });
            runForce = true;
            continue;
          }

          // #272 — the ONLY authority that retires a Polymarket cancel fence.
          // A successfully read, current-identity snapshot that no longer lists
          // an accepted ID is the venue confirming the removal. Deliberately not
          // called from the optimistic local removal in `cancel()`: that is this
          // client's guess, and treating it as authority would reopen the
          // duplicate window immediately.
          reconcilePolyCancels(
            out.orders.map((o) => String(o.id ?? o.orderID ?? o.orderId ?? '')),
            identity,
          );
          apply({
            orders: out.orders,
            status: 'ready',
            snapshotConfirmed: true,
            error: null,
            unavailableCode: null,
            lastFetchMs: nextLastFetchMs,
          });
        } else if (out.code) {
          emptyStreakRef.current = 0;
          // Debounced unavailable: a mount already showing orders holds
          // status+orders unchanged until the code proves persistent (a
          // transient coded 404 is real — credential edit mid-save). A mount
          // with no data flips immediately. The debounce reads only
          // current-identity state, so a switch never inherits A's streak.
          const visible = carry();
          unavailableStreakRef.current += 1;
          const holdSteady =
            visible.status === 'ready' &&
            visible.orders.length > 0 &&
            unavailableStreakRef.current < UNAVAILABLE_FLIP_STREAK;
          if (holdSteady) {
            // Stable rows are retained evidence, not a successful current read.
            apply({ ...visible, snapshotConfirmed: false, lastFetchMs: nextLastFetchMs });
          } else {
            apply({
              orders: [],
              status: 'unavailable',
              snapshotConfirmed: false,
              error: out.error,
              unavailableCode: out.code,
              lastFetchMs: nextLastFetchMs,
            });
          }
        } else {
          unavailableStreakRef.current = 0;
          emptyStreakRef.current = 0;
          apply({
            ...(out.status === 401 || out.status === 403 ? IDLE_ORDERS_STATE : carry()),
            status: 'error',
            snapshotConfirmed: false,
            error: out.error,
            unavailableCode: null,
            lastFetchMs: nextLastFetchMs,
          });
        }
        // A queued trailing pass exists only because a mutation landed
        // mid-fetch — it is forced by definition. It belongs to the MOUNTED
        // identity: a request that outlived its account must not consume a
        // force queued by its successor.
        runForce = true;
      } while (queuedForceRef.current && currentIdentityRef.current === identity);
    } finally {
      // Release only what this request owns: not another identity's latch,
      // and not a newer same-identity request's.
      if (inFlightRef.current === identity && !superseded()) inFlightRef.current = null;
    }
  }, [pollMs, identity, carry, apply]);

  const cancel = useCallback(
    async (orderId: string) => {
      const scope = capturePolyActionScope(identity);
      if (!scope.current()) return { ok: false as const, error: 'Account changed; reopen the order before cancelling.' };
      // #272 — through the app-wide in-flight fence, not the raw relay call.
      // This hook is mounted on several surfaces at once (Position Tracker plus
      // its docked prong, a Game Detail card), and each mount's optimistic
      // removal is LOCAL state: without a shared fence, two mounts — or one
      // mount racing a bulk sweep — each believe no cancel is running and both
      // submit the same DELETE.
      const out = await cancelPolyOrderOnce(orderId);
      if (!scope.current()) return { ok: false as const, error: 'Account changed during cancellation; check the original account before retrying.' };
      if (out.ok) {
        // Optimistic local removal — the next poll will reconcile if
        // CLOB hadn't yet propagated the cancel.
        applyOrders(
          carry().orders.filter((o) => {
            const id = o.id ?? o.orderID ?? o.orderId;
            return id !== orderId;
          }),
        );
        // Kick off a forced refresh to pick up the authoritative state.
        invalidateSharedOrders();
        void refresh(true);
        return { ok: true as const };
      }
      return {
        ok: false as const,
        error: out.error ?? 'Cancel failed.',
      };
    },
    [refresh, applyOrders, carry, identity],
  );

  // Replace = cancel + new-place. Polymarket has no atomic amend (same as
  // Kalshi). If the place fails after cancel succeeded, the caller is told
  // explicitly so the trader knows the original is gone.
  const replace = useCallback(
    async (
      orderId: string,
      newPriceCents: number,
      options: PolyRelayReplaceOptions = {},
    ): Promise<{ ok: true; orderId: string } | { ok: false; error: string }> => {
      // Every refusal before the cancel answers with the rejection tone, as a
      // Kalshi edit does; nothing has been sent and the original still rests.
      const refuse = (error: string): { ok: false; error: string } => {
        ringOrderRejected();
        return { ok: false, error };
      };
      const scope = capturePolyActionScope(identity);
      if (!scope.current()) return refuse('Account changed; reopen the order before replacing.');
      // (0, 100) exclusive — sub-cent prices (0.5¢) are valid on fine-tick
      // markets; tick misalignment surfaces as a CLOB rejection.
      if (!Number.isFinite(newPriceCents) || newPriceCents <= 0 || newPriceCents >= 100) {
        return refuse('Replacement price must be above 0 and below 100¢.');
      }
      const existing = carry().orders.find(
        (o) => (o.id ?? o.orderID ?? o.orderId) === orderId,
      );
      if (!existing) {
        return refuse('Order to replace was not found in the open-orders list.');
      }
      const tokenId = typeof existing.asset_id === 'string' ? existing.asset_id : null;
      if (!tokenId) {
        return refuse('Order has no asset_id — cannot replace.');
      }
      // SDK returns "BUY" / "SELL" strings; lowercase normalize. Default to
      // 'buy' if the field is missing — open orders we'd actually want to
      // replace are virtually always buys in this app's market-making flow.
      const sideStr = String(existing.side ?? '').toLowerCase();
      const action: 'buy' | 'sell' = sideStr === 'sell' ? 'sell' : 'buy';
      // Remaining size — the ONE shared derivation (polyOpenOrderRemaining:
      // original_size − size_matched, a positive legacy `size` read first;
      // the CLOB sends no `size`, #1397). CLOB allows fractional sizes, so
      // the replace leg preserves floats.
      let contracts = polyOpenOrderRemaining(existing) ?? NaN;
      if (!Number.isFinite(contracts) || contracts <= 0) {
        return refuse('Order remaining size is missing or invalid.');
      }
      // If the caller supplied an explicit nextQuantity, override the
      // derived remaining size with it. Validation here mirrors the Kalshi
      // accountStore.replaceOrder shape — integer ≥ 1 — even though Poly
      // CLOB technically accepts fractional sizes. The modal upstream
      // enforces integer-only entry for consistency with Kalshi's UX, so
      // a sub-integer nextQuantity here is a caller bug rather than user
      // input. One exception: the edit modal passes the remaining size itself
      // when the trader did not touch the quantity, which may be a fraction
      // (a partly filled order); it is re-placed exactly as it stands. The
      // caller may derive that size by subtraction (original − matched), so
      // float noise below a millionth of a contract still counts as the same.
      const untouchedQuantity =
        options.nextQuantity !== undefined && Math.abs(options.nextQuantity - contracts) < 1e-6;
      if (options.nextQuantity !== undefined && !untouchedQuantity) {
        const requested = options.nextQuantity;
        if (
          !Number.isFinite(requested) ||
          !Number.isInteger(requested) ||
          requested < 1
        ) {
          return refuse('Replacement quantity must be a positive integer ≥ 1.');
        }
        contracts = requested;
      }
      // The CLOB refuses an order under its minimum, and here that refusal
      // would land after the cancel. Includes a price-only edit of an order
      // that has already filled down below the minimum.
      if (contracts < POLY_MIN_ORDER_CONTRACTS) {
        return refuse(
          `Polymarket minimum is ${POLY_MIN_ORDER_CONTRACTS}; this edit would cancel the order and fail to re-place. Original kept.`,
        );
      }

      // FRESH sweep at click time: replace is non-atomic cancel-then-place,
      // so stale tickSize/negRisk here cancels the resting order and then
      // gets the re-place rejected (order + queue position destroyed). For
      // TICK the fresh sweep is the ONLY acceptable source (validated
      // below); for negRisk — which doesn't change per-market — the seeds /
      // open-order metadata remain the fallback when the sweep misses.
      const meta = describePolyOrder(tokenId);
      const existingNegRisk =
        typeof existing.negRisk === 'boolean' ? existing.negRisk : undefined;
      const negRisk = meta?.negRisk ?? options.negRisk ?? existingNegRisk;

      // Last-line tick revalidation BEFORE the cancel (DNL final pass):
      // ONLY the fresh market resolution counts as authoritative — Poly
      // tick regimes can change, so caller-seeded / open-order tickSize
      // metadata is NOT acceptable pre-cancel proof, and neither is the
      // unchanged current price (a resting order can predate a tick change;
      // recreating it at the same price can still be rejected AFTER the
      // cancel destroyed it). Fresh resolution missing → reject before
      // cancel, even for quantity-only edits.
      const freshTickSize = meta?.tickSize;
      const tickCheck = validateReplacePriceCents({
        newPriceCents,
        currentPriceCents: null, // existence is not tick-validity across regime changes
        // resolveTickCents, NOT tickCentsFor (#254): tickCentsFor is a
        // NEW-ORDER helper and answers 1¢ for a malformed tickSize, which let
        // an unproved whole-cent price through this gate and cancelled the
        // resting order on a grid nobody had confirmed. null → reject.
        tickCents: resolveTickCents(freshTickSize, undefined),
        unknownTickPolicy: 'reject',
      });
      if (!tickCheck.ok) {
        return refuse(tickCheck.error);
      }
      const tickSize = freshTickSize;

      // The replacement is post-only, and the CLOB refuses a post-only order
      // that would take, AFTER the cancel. Read the live book on the order's
      // own token first; an unknown book is not a refusal.
      const crossing = polyReplacementWouldCross(tokenId, meta?.pmTicker, action, newPriceCents);
      if (crossing === true) return refuse(EDIT_WOULD_CROSS_MESSAGE);

      // The replacement keeps the original's expiry. One that has passed or
      // sits inside the venue's GTD floor would be refused after the cancel,
      // so it is refused here, while the original still rests.
      const expirationMs = polyExpirationMs(existing);
      if (expirationMs != null && expirationMs - Date.now() <= POLY_GTD_REPLACE_MIN_LEAD_MS) {
        return refuse(
          `Not replaced: this order expires at ${polyTifLabel(existing, null)}, too soon to re-place. Original kept.`,
        );
      }

      const sourceError = sourceConfirmationError({ tokenId });
      if (sourceError) return refuse(sourceError);
      // 1) Cancel old. Bail before touching place if cancel rejects.
      const cancelOut = await cancelPolyOrderViaRelay(orderId);
      if (!cancelOut.ok) {
        return refuse(`Cancel failed: ${explainPolyError(cancelOut.error)}`);
      }
      // #1020 — place the replacement only when the venue confirms THIS order
      // was canceled. An order matched just before the cancel comes back as
      // not canceled; placing anyway would leave the fill AND a new order.
      const confirmed = polyCancelOutcome(cancelOut.result, orderId);
      if (confirmed.kind !== 'canceled') {
        invalidateSharedOrders();
        void refresh(true);
        return refuse(
          confirmed.kind === 'not_canceled'
            ? `Order was not canceled (${confirmed.reason}); it may have filled. No replacement was placed — check the order before editing again.`
            : 'The cancel result did not confirm this order was canceled. No replacement was placed — check the order before editing again.',
        );
      }
      if (!scope.current()) return refuse('Original order was cancelled, but the account changed. No replacement was placed; check the original account.');
      // Optimistic local removal so the UI doesn't render a doomed order
      // during the brief uncovered window.
      applyOrders(
        carry().orders.filter((o) => (o.id ?? o.orderID ?? o.orderId) !== orderId),
      );

      // 2) Place new at the new price. post_only=true keeps an edit from
      // accidentally crossing the spread. The expiry is the original's: a
      // good-till-start order repriced pregame must not rest into live play.
      const placeOut = await placePolyOrderViaRelay({
        tokenId,
        action,
        priceCents: newPriceCents,
        contracts,
        postOnly: true,
        tickSize,
        negRisk,
        ...(expirationMs != null ? { expirationTs: Math.floor(expirationMs / 1000) } : {}),
      });
      // The replacement's own tone, by the same rule as any placement:
      // accepted -> placed, refused -> rejected, unknown -> none.
      const placement = polyPlacementOutcome(placeOut);
      ringPolyPlacement(placement);
      if (!scope.current()) return { ok: false, error: 'Account changed after replacement submission. Check the original account to reconcile the result before retrying.' };
      // Always force-refresh so the open-orders list reconciles regardless
      // of whether the place succeeded.
      invalidateSharedOrders();
      void refresh(true);
      if (!placeOut.ok) {
        if (placement === 'unknown') return { ok: false, error: EDIT_REPLACEMENT_UNKNOWN_MESSAGE };
        return {
          ok: false,
          error: `Original order was cancelled, but the replacement failed to place: ${explainPolyError(placeOut.error)}`,
        };
      }
      // The relay echoes the CLOB OrderResponse as `result`; the id lands on
      // `orderID` (capital D), with `orderId`/`id` tolerated per the repo-wide
      // fallback. No confirmable id → the replacement may be resting, but
      // nothing downstream can key off it, so don't report a clean success —
      // the forced refresh above already reconciles the visible list.
      const placedRec = (placeOut.result ?? {}) as {
        orderID?: unknown;
        orderId?: unknown;
        id?: unknown;
      };
      const rawPlacedId = placedRec.orderID ?? placedRec.orderId ?? placedRec.id;
      const placedId =
        typeof rawPlacedId === 'string' && rawPlacedId.length > 0 ? rawPlacedId : null;
      if (!placedId) {
        return {
          ok: false,
          error:
            'Replacement was submitted but its order id could not be confirmed — refresh open orders to reconcile.',
        };
      }
      return { ok: true as const, orderId: placedId };
    },
    [carry, refresh, applyOrders, identity],
  );

  // Initial fetch + polling lifecycle. Polling slows to 1-in-
  // UNAVAILABLE_REPROBE_TICKS while the relay says "unavailable" — the
  // hook self-heals when the cred comes back; manual refresh still works.
  useEffect(() => {
    if (!enabled) return;

    let stopped = false;
    // Per-identity counters: an A-scoped queued force must not schedule
    // another A-stamped pass after the switch. (This also runs when the
    // effect re-arms on an enabled/pollMs change; dropping a same-identity
    // queued force there is intended — the next poll reconciles.)
    queuedForceRef.current = false;
    unavailableStreakRef.current = 0;
    emptyStreakRef.current = 0;
    // Identity isolation: ids are global and monotonic, so a counter left from
    // the previous account could only ever be LOWER — but resetting keeps the
    // rule "this mount has counted nothing for this identity yet" literally
    // true rather than accidentally true.
    countedReadSeqRef.current = 0;
    unavailableTicksRef.current = 0;
    void refresh();

    const tick = () => {
      if (stopped) return;
      // stateRef (not state) so the interval never needs re-creating on a
      // status change — and only when the stamp matches, so a stale
      // A-unavailable snapshot can't throttle B's polling.
      if (
        stateRef.current.identity === identity &&
        stateRef.current.status === 'unavailable'
      ) {
        unavailableTicksRef.current += 1;
        if (unavailableTicksRef.current % UNAVAILABLE_REPROBE_TICKS !== 0) return;
      } else {
        unavailableTicksRef.current = 0;
      }
      void refresh();
    };

    // 1/6 poll rate while the tab is hidden, immediate tick on refocus.
    const stopInterval = startVisibilityAwareInterval(tick, pollMs);
    return () => {
      stopped = true;
      stopInterval();
    };
    // `identity` is already represented through `refresh`; listing it
    // explicitly makes the lifecycle contract reviewable.
  }, [enabled, pollMs, refresh, identity]);

  return {
    ...current,
    refresh,
    cancel,
    replace,
  };
}
